import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const fetchCart = useCallback(async () => {
    if (!token) {
      localStorage.setItem('cart', JSON.stringify({ items: [], total: 0 }));
      setCart({ items: [], total: 0 });
      return;
    }
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
      localStorage.setItem('cart', JSON.stringify(response.data));
    } catch (error) {
      console.error('Error fetching cart:', error);
      const localCart = JSON.parse(localStorage.getItem('cart') || '{"items":[],"total":0}');
      setCart(localCart);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const mergeGuestCart = async () => {
      if (!token) return;
      const localCart = JSON.parse(localStorage.getItem('cart') || '{"items":[],"total":0}');
      if (!localCart.items?.length) return;

      try {
        for (const item of localCart.items) {
          await axios.post(
            `${API}/cart/add`,
            { product_id: item.product_id, quantity: item.quantity },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        localStorage.setItem('cart', JSON.stringify({ items: [], total: 0 }));
      } catch (error) {
        console.error('Error merging guest cart:', error);
      }
    };

    const sync = async () => {
      await mergeGuestCart();
      await fetchCart();
    };

    sync();
  }, [fetchCart, user, token]);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  const addToCart = async (productId, quantity = 1, product = null) => {
    if (!token) {
      const error = new Error('AUTH_REQUIRED');
      error.code = 'AUTH_REQUIRED';
      throw error;
    }

    try {
      await axios.post(`${API}/cart/add`, 
        { product_id: productId, quantity },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      await fetchCart();
      setIsOpen(true);
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  const updateQuantity = async (productId, quantity) => {
    if (!token) {
      const error = new Error('AUTH_REQUIRED');
      error.code = 'AUTH_REQUIRED';
      throw error;
    }

    try {
      await axios.put(`${API}/cart/update`,
        { product_id: productId, quantity },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
    }
  };

  const removeFromCart = async (productId) => {
    if (!token) {
      const error = new Error('AUTH_REQUIRED');
      error.code = 'AUTH_REQUIRED';
      throw error;
    }

    try {
      await axios.delete(`${API}/cart/remove/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const clearCart = async () => {
    if (!token) {
      const error = new Error('AUTH_REQUIRED');
      error.code = 'AUTH_REQUIRED';
      throw error;
    }

    try {
      await axios.delete(`${API}/cart/clear`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchCart();
    } catch (error) {
      console.error('Error clearing cart:', error);
    }
  };

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      isOpen,
      setIsOpen,
      addToCart,
      updateQuantity,
      removeFromCart,
      clearCart,
      fetchCart,
      itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
};
